import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import TestRailApiProjectPicker from '../../test-rail/components/TestRailApiProjectPicker';
import TestRailApiSectionPicker from '../../test-rail/components/TestRailApiSectionPicker';
import TestRailApiSuitePicker from '../../test-rail/components/TestRailApiSuitePicker';
import {
  TestRailApiAddCaseData,
  TestRailApiProject,
  TestRailApiSection,
  TestRailApiSuite,
  TestRailCase
} from '../../test-rail/TestRail.interface';
import { useCreateTaskV2TestRailTestCase } from '../TaskV2.hooks';

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  a {
    text-decoration: underline;
  }
`;

export interface TaskV2CreateTestRailCaseDialogProps {
  open: boolean;
  taskId: number;
  onCancel?: () => void;
  onCreate?: (testCase: TestRailCase) => void;
}

const TaskV2CreateTestRailCaseDialog: React.FC<
  TaskV2CreateTestRailCaseDialogProps
> = props => {
  const { open, taskId, onCancel, onCreate } = props;
  const [fields, setFields] = useState<Partial<TestRailApiAddCaseData>>({});
  const [selectedTestRailProject, setSelectedTestRailProject] =
    useState<TestRailApiProject | null>(null);
  const [selectedTestRailSuite, setSelectedTestRailSuite] =
    useState<TestRailApiSuite | null>(null);
  const [selectedTestRailSection, setSelectedTestRailSection] =
    useState<TestRailApiSection | null>(null);
  const { isLoading, error, createTestCase, abortCreateTestCase } =
    useCreateTaskV2TestRailTestCase();
  const valid = !!(
    selectedTestRailProject &&
    selectedTestRailSection &&
    fields.title
  );

  const handleCancel = () => !isLoading && onCancel?.();

  const create = async () => {
    if (
      isLoading ||
      !selectedTestRailProject ||
      !selectedTestRailSection ||
      !fields.title
    ) {
      return;
    }

    const { data } = await createTestCase({
      taskId,
      project: selectedTestRailProject,
      data: {
        ...fields,
        title: fields.title, // satisfy required prop type
        sectionId: selectedTestRailSection.id
      }
    });

    data && onCreate?.(data);
  };

  // abort create test case if the component is unmounted
  useEffect(() => {
    return () => abortCreateTestCase();
  }, [abortCreateTestCase]);

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={handleCancel}>
      {isLoading && <LinearProgress />}
      <DialogTitle>Create TestRail Test Case</DialogTitle>
      <form
        onSubmit={event => {
          event.preventDefault();
          create();
        }}
      >
        <StyledDialogContent>
          <TextField
            autoFocus
            fullWidth
            size='small'
            label='Title'
            variant='outlined'
            placeholder='Enter the Test Case title'
            disabled={isLoading}
            value={fields.title || ''}
            onChange={event => {
              setFields(fields => ({ ...fields, title: event.target.value }));
            }}
          />

          <TestRailApiProjectPicker
            disabled={isLoading}
            project={selectedTestRailProject}
            textFieldProps={{ size: 'small' }}
            onChange={value => {
              setSelectedTestRailProject(value);
              setSelectedTestRailSuite(null);
              setSelectedTestRailSection(null);
            }}
          />
          <TestRailApiSuitePicker
            suite={selectedTestRailSuite}
            testRailProjectId={selectedTestRailProject?.id}
            disabled={isLoading || !selectedTestRailProject}
            textFieldProps={{ size: 'small' }}
            onChange={value => {
              setSelectedTestRailSuite(value);
              setSelectedTestRailSection(null);
            }}
          />
          <TestRailApiSectionPicker
            section={selectedTestRailSection}
            testRailProjectId={selectedTestRailProject?.id}
            testRailSuiteId={selectedTestRailSuite?.id}
            disabled={
              isLoading || !selectedTestRailProject || !selectedTestRailSuite
            }
            textFieldProps={{
              size: 'small',
              helperText: selectedTestRailSection ? undefined : (
                <>
                  A section is required to create the test case. Please make
                  sure that at least one section is available in your{' '}
                  {selectedTestRailSuite ? (
                    <Link target='_blank' href={selectedTestRailSuite.url}>
                      TestRail suite
                    </Link>
                  ) : (
                    'TestRail suite'
                  )}{' '}
                  to proceed.
                </>
              )
            }}
            onChange={value => setSelectedTestRailSection(value)}
          />

          <Collapse in={!!error} style={{ minHeight: 'unset' }}>
            <Box mt={2}>
              <Alert severity='error'>
                {error?.message || 'An error occurred.'}
              </Alert>
            </Box>
          </Collapse>
        </StyledDialogContent>
        <DialogActions>
          <Button disabled={isLoading} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type='submit'
            color='primary'
            variant='contained'
            disabled={!valid || isLoading}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskV2CreateTestRailCaseDialog;
